import classNames from 'classnames';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { useCallback, useContext, useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerAuditLogDividendModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/AuditLogDividentModal.tsx';
import { DistributionReportsModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DistributionReportsModal.tsx';
import { IssuerAddDistributionModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddDistributionModal.tsx';
import {
  useDividendArchiveByIdMutation,
  useIssuerGenerateABAFileDividendMutation,
  useIssuerUpdateStatusDividendEventMutation,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { Button, ConfirmModal, ModalProviderContext, Spinner, TooltipNew } from 'ui';
import { InfoCircleBlueIcon, TrashGrayIcon } from 'ui/icons';
import { DividendEventExtendResponse, DividendStatus, UpdateDividendEventRequest } from 'api';

interface DistributionMoreProps {
  item: DividendEventExtendResponse;
  registerId: string;
  registerCountry: string;
  inModal?: boolean;
  isDeleted?: boolean;
}

export const DistributionMore = ({
  item,
  registerId,
  registerCountry,
  inModal = false,
  isDeleted = false,
}: DistributionMoreProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const isPending = item.status === DividendStatus.Pending;
  const [openContinue, setOpenContinue] = useState(false);
  const [openReport, setOpenReport] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const { mutateAsync: finaliseDividend, isLoading: isLoadingFinaliseDividend } =
    useIssuerUpdateStatusDividendEventMutation();

  const { mutateAsync: mutateAsyncDividendArchive, isLoading: isLoadingDividendArchive } =
    useDividendArchiveByIdMutation();

  const openAuditLog = useCallback(() => {
    setOpenLogs(true);
  }, []);

  const openReports = useCallback(() => {
    setOpenReport(true);
  }, []);

  const continueDistribution = useCallback(() => {
    setOpenContinue(true);
  }, []);

  const handleUpdateDividend = useCallback(
    (payload: UpdateDividendEventRequest) => {
      finaliseDividend({
        ...payload,
        register_id: item.register_obj.id,
      });
    },
    [item.register_obj.id, finaliseDividend],
  );

  const handleArchive = useCallback(
    (id: number) => {
      openModal(ConfirmModal, {
        title: `Archive ${getRegisterTypeStrings('dividend', { capitalize: false })}?`,
        onConfirm: async () => {
          try {
            setArchivingId(id);
            await mutateAsyncDividendArchive(id);
          } finally {
            setArchivingId(null);
          }
        },
      });
    },
    [getRegisterTypeStrings, mutateAsyncDividendArchive, openModal],
  );

  return (
    <>
      <div
        className={classNames(
          'flex flex-wrap sm:flex-nowrap gap-2 items-center sm:justify-end justify-start',
          {
            '': !inModal,
            'flex-col w-full': inModal,
          },
        )}
      >
        {/* {item?.reports?.[0]?.file_storage_item_url && ( */}
        {/*   <span className="cursor-pointer hover:opacity-80" onClick={e => e.stopPropagation()}> */}
        {/*     <a */}
        {/*       href={item?.reports?.[0]?.file_storage_item_url} */}
        {/*       target="_blank" */}
        {/*       rel="noopener noreferrer" */}
        {/*     > */}
        {/*       <img */}
        {/*         alt="cerificate" */}
        {/*         src={DownloadGreyIcon} */}
        {/*         className="w-6 h-6 min-w-[24px] min-h-[24px]" */}
        {/*       /> */}
        {/*     </a> */}
        {/*   </span> */}
        {/* )} */}
        <div className={classNames({ 'w-full': inModal })}>
          <Button
            variant={!inModal ? 'plain' : 'secondary'}
            className={classNames({ 'w-[80px] py-0 min-h-6 px-0': !inModal, 'w-full': inModal })}
            onClick={openReports}
          >
            Reports
          </Button>
        </div>
        <div className={classNames({ 'w-full': inModal })}>
          <Button
            variant={!inModal ? 'plain' : 'secondary'}
            className={classNames({ 'w-[80px] py-0 min-h-6 px-0': !inModal, 'w-full': inModal })}
            onClick={openAuditLog}
          >
            Audit log
          </Button>
        </div>
        <div className={classNames({ 'w-full': inModal })}>
          <ButtonDownloadABAFile
            dividendId={item.id}
            date={item.date}
            registerCountry={registerCountry}
            className={classNames({ 'py-0 min-h-6': !inModal, 'w-full px-4': inModal })}
            variant={!inModal ? 'plain' : 'secondary'}
          />
        </div>
        {isPending && !isDeleted && (
          <div className={classNames({ 'w-full': inModal })}>
            {item.operation_statuses?.payments === true &&
              item.operation_statuses?.drps === true && (
                <TooltipNew
                  text="Press this button after updating the NAV price to calculate the quantity and price
                  for all DRP transactions in Dividend Pending status. These will then be moved to
                  Completed status."
                  variant={'info'}
                >
                  <Button
                    className={classNames({
                      'w-[75px] py-0 min-h-6 px-0': !inModal,
                      'w-full': inModal,
                    })}
                    variant={!inModal ? 'plain' : 'primary'}
                    onClick={() =>
                      handleUpdateDividend({
                        id: item.id,
                      })
                    }
                    disabled={isLoadingFinaliseDividend}
                    loading={isLoadingFinaliseDividend}
                  >
                    Finalise
                  </Button>
                </TooltipNew>
              )}
            {!(
              item.operation_statuses?.payments === true && item.operation_statuses?.drps === true
            ) && (
              <Button
                variant={!inModal ? 'plain' : 'primary'}
                className={classNames({
                  'w-[70px] px-0 py-0 min-h-6': !inModal,
                  'w-full': inModal,
                })}
                onClick={continueDistribution}
              >
                Continue
              </Button>
            )}
          </div>
        )}
        {!inModal && !isDeleted && (
          <span>
            {isLoadingDividendArchive && archivingId === item.id ? (
              <Spinner size="sm" className="m-0" />
            ) : (
              <TooltipNew text="Archive" className="w-5 h-5 min-w-5 min-h-5">
                <img
                  src={TrashGrayIcon}
                  alt="Archive"
                  className="w-5 h-5 min-w-5 min-h-5 cursor-pointer hover:opacity-75"
                  onClick={e => {
                    e.stopPropagation();
                    handleArchive(item.id);
                  }}
                />
              </TooltipNew>
            )}
          </span>
        )}
      </div>
      {openContinue && (
        <IssuerAddDistributionModal
          open={openContinue}
          onClose={() => setOpenContinue(false)}
          {...{
            registerCountry,
            register_id: +registerId,
            selectedClass: {
              label: item.register_class.class_name,
              value: {
                id: item.register_class_id,
                price_per_share: item.register_class.current_price_per_share,
                toString: () => item.register_class.class_name,
              },
            },
            modalStep: !item.operation_statuses?.payments ? 3 : 4,
            existDividendEventId: item.id,
            dividendDate: item.date,
          }}
        />
      )}
      {openReport && (
        <DistributionReportsModal
          open={openReport}
          onClose={() => setOpenReport(false)}
          {...{
            dividendId: item.id,
            registerId: item.register_obj?.id || Number(registerId),
            reports: item.reports,
            registerClass: item.register_class,
          }}
        />
      )}
      {openLogs && (
        <IssuerAuditLogDividendModal
          open={openLogs}
          onClose={() => setOpenLogs(false)}
          {...{
            dividend_event_id: item.id,
            registerId,
            isDeleted,
          }}
        />
      )}
    </>
  );
};

interface ButtonDownloadABAFileProps {
  // ISO Date: example new Date().toISOString()
  date: string;
  dividendId: number;
  className?: string;
  registerCountry: string;
  variant?: 'primary' | 'secondary' | 'plain';
}

export const ButtonDownloadABAFile = ({
  date,
  dividendId,
  className,
  registerCountry,
  variant = 'plain',
}: ButtonDownloadABAFileProps) => {
  const handleDownload = useCallback(
    (base64file?: string) => {
      if (!base64file) return;

      const parsedDate = parseISO(String(date));
      const fileName = `${parsedDate ? format(parsedDate, 'yyyyMMdd') : 'file'}.ABA`;

      const link = document.createElement('a');
      link.href = base64file;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
    },
    [date],
  );

  const { mutateAsync: generateABAFile } = useIssuerGenerateABAFileDividendMutation({
    onSuccess: handleDownload,
  });

  return (
    <>
      {registerCountry === 'AUS' && (
        <Button
          onClick={() => {
            generateABAFile(dividendId);
          }}
          className={classNames(
            'flex gap-1',
            {
              'px-0': !className?.includes('px-'),
              'w-[165px]': !className?.includes('w-'),
            },
            className,
          )}
          variant={variant}
        >
          <span>Download ABA File</span>
          <TooltipNew
            text="Download .ABA file. You can upload it to your bank to send a payment to every investor
              who got cash distribution."
            variant={'info'}
          >
            <img
              alt="info"
              src={InfoCircleBlueIcon}
              className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
            />
          </TooltipNew>
          {/* <Tooltip */}
          {/*   position="center" */}
          {/*   positionHorizontal="left" */}
          {/*   variant="gray" */}
          {/*   delayTimeOpen={0} */}
          {/*   delayTimeClose={0} */}
          {/*   classNameTooltip="w-[300px]" */}
          {/*   button={ */}
          {/*     <img */}
          {/*       alt="info" */}
          {/*       src={InfoCircleBlueIcon} */}
          {/*       className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]" */}
          {/*     /> */}
          {/*   } */}
          {/* > */}
          {/*   <span> */}
          {/*     Download .ABA file. You can upload it to your bank to send a payment to every investor */}
          {/*     who got cash distribution. */}
          {/*   </span> */}
          {/* </Tooltip> */}
        </Button>
      )}
    </>
  );
};
